import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUiTranslation } from "@tubo/ui/i18n";
import { ArrowLeft, CircleCheck, Download, Save, Sparkles, Trash2, Upload } from "lucide-react";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { Textarea } from "@tubo/ui/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tubo/ui/components/ui/alert-dialog";
import {
  useValueVocabularyDetail,
  useUpdateItems,
  useImportCsv,
  useExportCsv,
  usePublishVocabulary,
  getValueVocabularyDetailQueryKey,
  getValueVocabularyListQueryKey,
} from "@tubo/api-models/hooks/value-vocabulary-api.hooks";
import { useSemanticConceptList } from "@tubo/api-models/hooks/semantic-concept-api.hooks";
import type { ConceptLinkSuggestionVO, VocabularyItemVO } from "@tubo/api-models/hooks/value-vocabulary-api.schemas";
import { VocabularyItemStatusEnum } from "@tubo/api-models/business/semantic/semantic.enums";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import { ConceptLinkSelectDialog } from "@client/components/semantic-platform/concept-link-select-dialog";
import { VocabularyConceptSuggestDialog } from "@client/components/semantic-platform/vocabulary-concept-suggest-dialog";
import { VocabularyBasicInfoPanel } from "@client/components/semantic-platform/vocabulary-basic-info-panel";
import { VocabularyDeleteDialog } from "@client/components/semantic-platform/vocabulary-delete-dialog";
import { VocabularyItemsTable } from "@client/components/semantic-platform/vocabulary-items-table";
import { publishStatusVariant } from "@client/components/semantic-platform/publish-status";
import { ResourcePublishStatusEnum } from "@tubo/api-models/business/platform/platform.enums";

const PUBLISH_STATUS_KEY: Record<string, "draft" | "pendingReview" | "published" | "deprecated"> = {
  [ResourcePublishStatusEnum.Draft]: "draft",
  [ResourcePublishStatusEnum.PendingReview]: "pendingReview",
  [ResourcePublishStatusEnum.Published]: "published",
  [ResourcePublishStatusEnum.Deprecated]: "deprecated",
};

export const Route = createFileRoute("/_app/semantic-platform/vocabularies/$id")({
  component: VocabularyDetailPage,
});

/** 生成词条 ID:UUID(全局唯一;code 仅作内部唯一键,展示与父级选择均走显示名) */
function generateItemCode(): string {
  return crypto.randomUUID();
}

function VocabularyDetailPage() {
  const { t } = useUiTranslation("semanticPlatformDetails");
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { searchStr } = useLocation();
  const listSearch = new URLSearchParams(searchStr).get("ns");
  const queryClient = useQueryClient();
  const { data: vocab } = useValueVocabularyDetail(id, { query: {} });
  const { data: csvText } = useExportCsv(id, { query: {} });

  // 本地编辑态(items 整组提交;服务端环/自反校验)
  const [items, setItems] = useState<VocabularyItemVO[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [csvDraft, setCsvDraft] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemDeleteIdx, setItemDeleteIdx] = useState<number | null>(null);
  // 概念挂接(P2a 手动挂接 / P3 AI 推荐)
  const [pickIdx, setPickIdx] = useState<number | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const { data: conceptPage } = useSemanticConceptList(
    { page: 1, pageSize: 500 },
    { query: {} },
  );
  const conceptLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of conceptPage?.items ?? []) map.set(c.code, c.preferredLabel);
    return map;
  }, [conceptPage]);

  useEffect(() => {
    if (vocab?.items) setItems(vocab.items);
  }, [vocab]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: getValueVocabularyDetailQueryKey(id) });

  const { mutate: saveItems, isPending: savingItems } = useUpdateItems({
    mutation: {
      onSuccess: () => { invalidate(); toast.success(t("vocabularyDetail.itemSaved")); },
      onError: () => toast.error(t("vocabularyDetail.saveFailed")),
    },
  });
  const publishMutation = usePublishVocabulary({
    mutation: {
      onSuccess: (detail) => {
        invalidate();
        queryClient.invalidateQueries({ queryKey: getValueVocabularyListQueryKey() });
        toast.success(t("vocabularyDetail.published", { name: detail?.name ?? t("vocabularyDetail.title") }));
      },
      onError: () => toast.error(t("vocabularyDetail.publishFailed")),
    },
  });
  const importMutation = useImportCsv({
    mutation: {
      onSuccess: (res) => {
        invalidate();
        toast.success(t("vocabularyDetail.imported", { created: res.created, updated: res.updated, skipped: res.skipped }));
        setImportOpen(false);
        setCsvDraft("");
      },
      onError: () => toast.error(t("vocabularyDetail.importFailed")),
    },
  });

  const dirty = vocab ? JSON.stringify(items) !== JSON.stringify(vocab.items ?? []) : false;

  const addItem = () =>
    setItems((prev) => [...prev, { code: generateItemCode(), preferredLabel: "", sortOrder: prev.length + 1 }]);
  const patchItem = (idx: number, patch: Partial<VocabularyItemVO>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const toggleItemStatus = (idx: number) =>
    patchItem(idx, {
      status:
        items[idx]?.status === VocabularyItemStatusEnum.Deprecated
          ? VocabularyItemStatusEnum.Active
          : VocabularyItemStatusEnum.Deprecated,
    });
  const clearConcept = (idx: number) => patchItem(idx, { conceptCode: undefined });
  const applySuggestions = (picked: ConceptLinkSuggestionVO[]) => {
    const conceptByItemCode = new Map(picked.map((s) => [s.itemCode, s.conceptCode]));
    setItems((prev) => prev.map((it) => {
      const conceptCode = conceptByItemCode.get(it.code);
      return conceptCode ? { ...it, conceptCode } : it;
    }));
    toast.success(t("vocabularyDetail.linksSaved", { count: picked.length }));
  };
  const unlinkedCount = items.filter(
    (i) => !i.conceptCode && i.status !== VocabularyItemStatusEnum.Deprecated,
  ).length;

  const downloadCsv = useCallback(() => {
    if (!csvText || !vocab) return;
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${vocab.code}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [csvText, vocab]);

  // 头部注入域级 SecondaryNav(标题区:返回/ID/名称;操作区:导入导出/保存/删除),页面体不再自写标题栏
  const headerTitle = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" aria-label={t("vocabularyDetail.backToList")} onClick={() => navigate({ to: "/semantic-platform/vocabularies", search: listSearch ? { ns: listSearch } : {} })}>
          <ArrowLeft className="size-4" />
        </Button>
        {vocab ? (
          <>
            <span className="font-mono text-sm text-muted-foreground">{vocab.code}</span>
            <span className="text-sm font-semibold">{vocab.name}</span>
            {vocab.isHierarchical && <Badge variant="outline">{t("vocabularyDetail.hierarchy")}</Badge>}
            <Badge variant={publishStatusVariant(vocab.status)}>{PUBLISH_STATUS_KEY[vocab.status] ? t(`vocabularyDetail.status.${PUBLISH_STATUS_KEY[vocab.status]}`) : vocab.status}</Badge>
          </>
        ) : (
          <span className="text-sm font-semibold">{t("vocabularyDetail.title")}</span>
        )}
      </div>
    ),
    [vocab, navigate, listSearch, t],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () =>
      vocab ? (
        <div className="flex items-center gap-2">
          {vocab.status !== "published" && (
            <Button variant="outline" size="sm" disabled={publishMutation.isPending} onClick={() => publishMutation.mutate({ id })}>
              <CircleCheck className="mr-1 size-4" /> {t("vocabularyDetail.publish")}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="mr-1 size-4" /> {t("vocabularyDetail.importCsv")}
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv}>
            <Download className="mr-1 size-4" /> {t("vocabularyDetail.exportCsv")}
          </Button>
          <Button size="sm" disabled={!dirty || savingItems} onClick={() => saveItems({ id, data: { items } })}>
            <Save className="mr-1 size-4" /> {t("vocabularyDetail.saveItems")}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("vocabularyDetail.delete")}
            title={t("vocabularyDetail.delete")}
            className="text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      ) : null,
    [vocab, dirty, savingItems, saveItems, downloadCsv, id, items, publishMutation, t],
  );
  useSecondaryNavActions(headerActions);

  if (!vocab) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">{t("vocabularyDetail.loading")}</div>;
  }

  const itemDeleteTarget = itemDeleteIdx !== null ? items[itemDeleteIdx] : null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto p-4 xl:p-6">
        <div className="space-y-4">
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <VocabularyBasicInfoPanel vocabulary={vocab} onSaved={invalidate} />

            <section className="flex h-full min-h-0 flex-col rounded-lg border bg-surface/50 p-4">
              <h2 className="mb-3 text-sm font-semibold">{t("vocabularyDetail.projectionTitle")}</h2>
              <p className="mb-2 text-xs text-muted-foreground">
                {t("vocabularyDetail.projectionDescription")}
              </p>
              <div className="min-h-0 flex-1 space-y-1 overflow-auto rounded bg-muted/40 p-2 font-mono text-xs">
                {(vocab.enumOptions ?? []).map((opt) => (
                  <div key={opt} className="px-1">"{opt}"</div>
                ))}
                {(vocab.enumOptions ?? []).length === 0 && <span className="text-muted-foreground">[]</span>}
              </div>
            </section>
          </div>

          <VocabularyItemsTable
            items={items}
            hierarchical={Boolean(vocab.isHierarchical)}
            conceptLabelByCode={conceptLabelByCode}
            onPickConcept={setPickIdx}
            onClearConcept={clearConcept}
            actions={
              <Button
                variant="outline"
                size="sm"
                disabled={unlinkedCount === 0}
                title={unlinkedCount === 0 ? t("vocabularyDetail.noUnlinked") : t("vocabularyDetail.suggestTitle")}
                onClick={() => setSuggestOpen(true)}
              >
                <Sparkles className="mr-1 size-4" /> {t("vocabularyDetail.suggest")}
              </Button>
            }
            onAdd={addItem}
            onPatch={patchItem}
            onToggleStatus={toggleItemStatus}
            onDelete={setItemDeleteIdx}
          />
        </div>
      </div>

      <ConceptLinkSelectDialog
        open={pickIdx !== null}
        onOpenChange={(open) => !open && setPickIdx(null)}
        onPick={(concept) => {
          if (pickIdx !== null) {
            patchItem(pickIdx, { conceptCode: concept.code });
            toast.success(t("vocabularyDetail.linked", { name: concept.preferredLabel }));
          }
        }}
      />

      <VocabularyConceptSuggestDialog
        vocabularyId={id}
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        onApply={applySuggestions}
      />

      <AlertDialog open={itemDeleteIdx !== null} onOpenChange={(open) => !open && setItemDeleteIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("vocabularyDetail.deleteItemTitle", { name: itemDeleteTarget?.preferredLabel || itemDeleteTarget?.code })}</AlertDialogTitle>
            <AlertDialogDescription>{t("vocabularyDetail.deleteItemDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("vocabularyDetail.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => itemDeleteIdx !== null && removeItem(itemDeleteIdx)}
            >
              {t("vocabularyDetail.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <VocabularyDeleteDialog
        target={deleteOpen ? { id: vocab.id, code: vocab.code, name: vocab.name } : null}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => navigate({ to: "/semantic-platform/vocabularies", search: listSearch ? { ns: listSearch } : {} })}
      />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("vocabularyDetail.importTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{t("vocabularyDetail.importDescription")}</p>
          <Textarea rows={8} value={csvDraft} onChange={(e) => setCsvDraft(e.target.value)} placeholder={t("vocabularyDetail.csvExample")} />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setImportOpen(false)}>{t("vocabularyDetail.cancel")}</Button>
            <Button size="sm" disabled={!csvDraft.trim() || importMutation.isPending} onClick={() => importMutation.mutate({ id, data: { csv: csvDraft } })}>
              {t("vocabularyDetail.import")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
